import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  createBeneficiarySlice,
  type BeneficiarySlice,
  getInitialBeneficiaryState,
} from './slices/beneficiarySlice';
import {
  createCoverageSlice,
  type CoverageSlice,
  getInitialCoverageState,
} from './slices/coverageSlice';
import {
  createFormSlice,
  type FormSlice,
  getInitialFormState,
} from './slices/formSlice';
import {
  createPurchaseSlice,
  type PurchaseSlice,
  getInitialPurchaseState,
} from './slices/purchaseSlice';
import {
  createUISlice,
  type UISlice,
  getInitialUIState,
} from './slices/uiSlice';
import { createSelectors } from './util';

/**
 * Combined application store type - intersection of all slices
 */
export type AppStore = FormSlice &
  CoverageSlice &
  BeneficiarySlice &
  UISlice &
  PurchaseSlice;

/**
 * ================================================================================================
 * SELECTOR PATTERN GUIDELINES
 * ================================================================================================
 *
 * This application uses three distinct patterns for accessing Zustand store state.
 * Follow these guidelines to maintain consistency and optimize performance:
 *
 * ------------------------------------------------------------------------------------------------
 * 1. AUTO-GENERATED .use HOOKS - For simple state/action access
 * ------------------------------------------------------------------------------------------------
 * USE FOR: Accessing single properties or actions from the store
 *
 * PATTERN:
 * ```typescript
 * const value = useAppStore.use.propertyName()
 * const action = useAppStore.use.actionName()
 * ```
 *
 * EXAMPLES:
 * ```typescript
 * const backgroundInfoForm = useAppStore.use.backgroundInfoForm()
 * const setBackgroundInfoForm = useAppStore.use.setBackgroundInfoForm()
 * const selectedCoverages = useAppStore.use.selectedCoverages()
 * const toggleSelectedCoverages = useAppStore.use.toggleSelectedCoverages()
 * ```
 *
 * BENEFITS:
 * - Clean, consistent syntax across the codebase
 * - Auto-generated by createSelectors utility
 * - Type-safe
 * - Optimized re-render behavior for single properties
 *
 * ------------------------------------------------------------------------------------------------
 * 2. useShallow HOOK - For multiple properties with shallow comparison
 * ------------------------------------------------------------------------------------------------
 * USE FOR: Selecting multiple related properties together to prevent unnecessary re-renders
 *
 * PATTERN:
 * ```typescript
 * import { useShallow } from 'zustand/react/shallow'
 *
 * const { prop1, prop2, action1 } = useAppStore(
 *   useShallow((state) => ({
 *     prop1: state.prop1,
 *     prop2: state.prop2,
 *     action1: state.action1,
 *   }))
 * )
 * ```
 *
 * EXAMPLES:
 * ```typescript
 * const { selectedBeneficiary, namedBeneficiaries } = useAppStore(
 *   useShallow((state) => ({
 *     selectedBeneficiary: state.selectedBeneficiary,
 *     namedBeneficiaries: state.namedBeneficiaries,
 *   }))
 * )
 * ```
 *
 * BENEFITS:
 * - Prevents re-renders when object/array references change but contents are the same
 * - Groups related state access together
 * - More efficient than multiple individual selectors when values change together
 *
 * AVOID: Using useShallow for single properties - use .use hooks instead
 *
 * ------------------------------------------------------------------------------------------------
 * 3. CUSTOM MEMOIZED SELECTORS - For computed/derived values
 * ------------------------------------------------------------------------------------------------
 * USE FOR: Computed values that derive from store state with complex logic
 *
 * PATTERN:
 * ```typescript
 * // In state/selectors/someSelectors.ts
 * import { createSelector } from 'reselect'
 * import { useAppStore, type AppStore } from '../store'
 *
 * // Base selector
 * const selectBaseProperty = (state: AppStore) => state.baseProperty
 *
 * // Memoized computed selector
 * export const selectComputedValue = createSelector(
 *   [selectBaseProperty],
 *   (baseProperty) => {
 *     // Complex computation here
 *     return computedResult
 *   }
 * )
 *
 * // Usage in components
 * const computedValue = useAppStore(selectComputedValue)
 * ```
 *
 * EXAMPLES:
 * ```typescript
 * // Calculate total percentage of all named beneficiaries
 * export const selectNamedBeneficiariesTotalPercentage = createSelector(
 *   [selectNamedBeneficiaries],
 *   (namedBeneficiaries) => {
 *     return namedBeneficiaries.reduce((sum, b) => sum + (b.percentage ?? 0), 0)
 *   }
 * )
 *
 * // Derive Premium objects from coverage selections
 * export const selectSelectedPremiums = createSelector(
 *   [selectCoveragePremiums, selectCoverageSelections],
 *   (coveragePremiums, coverageSelections) => {
 *     // Complex mapping logic...
 *   }
 * )
 *
 * // In component
 * const totalPercentage = useAppStore(selectNamedBeneficiariesTotalPercentage)
 * const selectedPremiums = useSelectedPremiums() // Can wrap in convenience hook
 * ```
 *
 * BENEFITS:
 * - Memoization prevents unnecessary recalculation
 * - Keeps business logic out of components
 * - Reusable across multiple components
 * - Testable independently
 *
 * WHEN TO CREATE: Only when there's actual computation/derivation logic. Don't create
 * wrapper functions that just call .use hooks - that adds unnecessary indirection.
 *
 * ------------------------------------------------------------------------------------------------
 * 4. PARAMETERIZED SELECTORS - For dynamic computed values
 * ------------------------------------------------------------------------------------------------
 * USE FOR: Selectors that need parameters (e.g., filtering by ID or type)
 *
 * PATTERN:
 * ```typescript
 * export const useParameterizedSelector = (param: string): ResultType => {
 *   const stateValue = useAppStore.use.stateValue()
 *   // Use param to compute result
 *   return computedResult
 * }
 * ```
 *
 * EXAMPLE:
 * ```typescript
 * export const useSelectablePremiums = (coverageProduct: CoverageProduct): Premium[] => {
 *   const coveragePremiums = useAppStore.use.coveragePremiums()
 *   const selectedCoverages = useAppStore.use.selectedCoverages()
 *   // Filter/compute based on coverageProduct parameter
 *   return filteredPremiums
 * }
 * ```
 *
 * NOTE: These must be hook functions since they call other hooks internally
 *
 * Usage examples:
 * ```typescript
 * import { useAppStore } from 'state/store'
 * import { useShallow } from 'zustand/react/shallow'
 * import { selectNamedBeneficiariesTotalPercentage } from 'state/selectors/beneficiarySelectors'
 *
 * // Simple state access - use .use hooks
 * const backgroundInfoForm = useAppStore.use.backgroundInfoForm()
 * const setBackgroundInfoForm = useAppStore.use.setBackgroundInfoForm()
 *
 * // Multiple related properties - use useShallow
 * const { selectedBeneficiary, namedBeneficiaries } = useAppStore(
 *   useShallow((state) => ({
 *     selectedBeneficiary: state.selectedBeneficiary,
 *     namedBeneficiaries: state.namedBeneficiaries,
 *   }))
 * )
 *
 * // Computed values - use custom memoized selectors
 * const totalPercentage = useAppStore(selectNamedBeneficiariesTotalPercentage)
 *
 * // Outside React - direct access
 * const currentForm = useAppStore.getState().backgroundInfoForm
 * useAppStore.getState().setBackgroundInfoForm(newForm)
 * ```
 */
const useAppStoreBase = create<AppStore>()(
  devtools(
    (set, get, api) => ({
      ...createFormSlice(set, get, api),
      ...createCoverageSlice(set, get, api),
      ...createBeneficiarySlice(set, get, api),
      ...createUISlice(set, get, api),
      ...createPurchaseSlice(set, get, api),
    }),
    {
      name: 'LifePurchaseStore',
      enabled: process.env.NODE_ENV === 'development',
    }
  )
);

/**
 * Enhanced store with auto-generated .use hooks for each state property
 *
 * The createSelectors utility adds hooks like:
 * - useAppStore.use.backgroundInfoForm()
 * - useAppStore.use.setBackgroundInfoForm()
 */
export const useAppStore = createSelectors(useAppStoreBase);

/**
 * Reset store to initial state (helper for tests)
 *
 * Resets all slices to their initial values by calling each slice's initialization function
 */
export const resetStore = (): void => {
  useAppStore.setState({
    ...getInitialFormState(),
    ...getInitialCoverageState(),
    ...getInitialBeneficiaryState(),
    ...getInitialUIState(),
    ...getInitialPurchaseState(),
  });
};

// Re-export types for convenience
export type { FormSlice } from './slices/formSlice';
export type { CoverageSlice } from './slices/coverageSlice';
export type { BeneficiarySlice } from './slices/beneficiarySlice';
export type { UISlice } from './slices/uiSlice';
export type { PurchaseSlice } from './slices/purchaseSlice';

// Re-export initialization functions for tests and utilities
export {
  getInitialFormState,
  getInitialBackgroundInfo,
} from './slices/formSlice';
export { getInitialCoverageState } from './slices/coverageSlice';
export { getInitialBeneficiaryState } from './slices/beneficiarySlice';
export { getInitialUIState } from './slices/uiSlice';
export { getInitialPurchaseState } from './slices/purchaseSlice';
